// src/Components/dashboard/design-system.js
// ─────────────────────────────────────────────────────────────────────────────
// Shared design tokens and micro-components for the Beetroot Analytics Suite.
// Import from here in every page to keep the look 100% consistent.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// ── Brand palette (mirrors Tailwind config + custom props) ────────────────────
export const BRAND = {
  red:     '#C8102E',   // Beetroot primary
  redDark: '#A00D24',
  redSoft: '#FEE2E2',
  gold:    '#F59E0B',
  emerald: '#10B981',
  sky:     '#0EA5E9',
  violet:  '#7C3AED',
  slate:   '#1E293B',
};

// ── Chart colour palette ──────────────────────────────────────────────────────
export const CHART_COLORS = [
  '#C8102E', '#0EA5E9', '#10B981', '#F59E0B',
  '#7C3AED', '#EC4899', '#14B8A6', '#F97316',
];

// ── Number formatter ──────────────────────────────────────────────────────────
export const fmt = (n) => {
  if (n == null || n === '') return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
export const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl ${className}`} />
);

// ── Trend badge ───────────────────────────────────────────────────────────────
export const TrendBadge = ({ value, direction, size = 'sm' }) => {
  if (!value && value !== 0) return null;
  const isUp   = direction === 'up'   || (typeof value === 'number' && value > 0);
  const isDown = direction === 'down' || (typeof value === 'number' && value < 0);
  const label  = typeof value === 'number'
    ? `${value > 0 ? '+' : ''}${value}%`
    : value;

  const base = size === 'lg'
    ? 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold'
    : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

  if (isUp)   return <span className={`${base} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400`}><TrendingUp size={size === 'lg' ? 13 : 10} />{label}</span>;
  if (isDown) return <span className={`${base} bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400`}><TrendingDown size={size === 'lg' ? 13 : 10} />{label}</span>;
  return       <span className={`${base} bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400`}><Minus size={size === 'lg' ? 13 : 10} />{label}</span>;
};

// ── Section header ────────────────────────────────────────────────────────────
export const SectionHeader = ({ title, sub, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">{title}</h2>
      {sub && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {action}
  </div>
);

// ── Card wrapper ──────────────────────────────────────────────────────────────
export const Card = ({ children, className = '', onClick, hover = false }) => (
  <div
    onClick={onClick}
    className={`
      bg-white dark:bg-gray-900
      border border-gray-100 dark:border-gray-800
      rounded-2xl shadow-sm
      ${hover ? 'hover:shadow-md hover:border-red-200 dark:hover:border-red-900 cursor-pointer transition-all duration-200 hover:-translate-y-0.5' : ''}
      ${className}
    `}
  >
    {children}
  </div>
);

// ── Realtime pill ─────────────────────────────────────────────────────────────
export const RealtimePill = ({ active }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
    active
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
    {active ? 'Live' : 'Offline'}
  </span>
);

// ── Empty state ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
      <Icon size={26} className="text-gray-300 dark:text-gray-600" />
    </div>
    <p className="text-sm font-medium text-gray-400 dark:text-gray-500">{title}</p>
    {sub && <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{sub}</p>}
  </div>
);

// ── Rank badge ────────────────────────────────────────────────────────────────
export const RankBadge = ({ rank }) => {
  const styles = {
    1: 'bg-amber-400 text-amber-900',
    2: 'bg-gray-300 text-gray-700',
    3: 'bg-orange-400 text-orange-900',
  };
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
      styles[rank] || 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      {rank}
    </div>
  );
};

// ── Page header ───────────────────────────────────────────────────────────────
export const PageHeader = ({ title, sub, icon: Icon, children, lastUpdated, realtime }) => (
  <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon size={20} className="text-red-600" />
        </div>
      )}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2">
          {sub}
          {lastUpdated && (
            <span className="text-gray-300 dark:text-gray-600">·</span>
          )}
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      {realtime != null && <RealtimePill active={realtime} />}
      {children}
    </div>
  </div>
);

// ── Action buttons ────────────────────────────────────────────────────────────
export const RefreshBtn = ({ onClick, loading }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-red-400 hover:text-red-600 transition-colors"
  >
    <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
    Refresh
  </button>
);

export const ExportBtn = ({ onClick, label = 'Export CSV' }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-sm shadow-red-200 dark:shadow-none"
  >
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
    {label}
  </button>
);